export function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

export function formatSummaryName(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';

  if (rawValue.includes('@')) {
    return rawValue;
  }

  const parts = rawValue.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';

  const firstName = parts[0];
  const firstNameFormatted = `${firstName.charAt(0).toUpperCase()}${firstName.slice(1).toLowerCase()}`;

  if (parts.length === 1) {
    return firstNameFormatted;
  }

  const lastName = parts[parts.length - 1];
  const lastInitial = lastName.charAt(0).toUpperCase();

  return `${firstNameFormatted} ${lastInitial}.`;
}

export function isParticipatingAttendance(attendance) {
  const normalizedAttendance = String(attendance || '').trim().toLowerCase();
  return normalizedAttendance === 'present' || normalizedAttendance === 'late';
}

export function normalizeTeamIndex(value) {
  const normalized = Number(value);
  return normalized === 0 || normalized === 1 ? normalized : null;
}

export function sortMatchEvents(matchEvents = []) {
  return [...(Array.isArray(matchEvents) ? matchEvents : [])].sort((left, right) => {
    if ((left?.minute || 0) !== (right?.minute || 0)) {
      return (left?.minute || 0) - (right?.minute || 0);
    }

    return (left?.sortIndex || 0) - (right?.sortIndex || 0);
  });
}

export function getGoalTotals(matchEvents = []) {
  const score = [0, 0];
  let hasGoalEvent = false;

  (Array.isArray(matchEvents) ? matchEvents : []).forEach((event) => {
    if (event?.type !== 'goal') return;

    const teamIndex = normalizeTeamIndex(event?.teamIndex);
    if (teamIndex === null) return;

    score[teamIndex] += 1;
    hasGoalEvent = true;
  });

  return { score, hasGoalEvent };
}

export function deriveOutcomeFromScore(team1Goals, team2Goals) {
  if (Number(team1Goals) > Number(team2Goals)) {
    return 'Team 1 wins';
  }

  if (Number(team2Goals) > Number(team1Goals)) {
    return 'Team 2 wins';
  }

  return 'Draw';
}

export function resolveDisplayedScore(result, matchEvents) {
  const { score: eventScore, hasGoalEvent } = getGoalTotals(matchEvents);
  const storedTeam1Goals = Number(result?.team1Goals) || 0;
  const storedTeam2Goals = Number(result?.team2Goals) || 0;
  const [eventTeam1Goals, eventTeam2Goals] = eventScore;
  const hasScoreMismatch =
    hasGoalEvent &&
    (eventTeam1Goals !== storedTeam1Goals || eventTeam2Goals !== storedTeam2Goals);

  if (hasScoreMismatch) {
    return {
      team1Goals: eventTeam1Goals,
      team2Goals: eventTeam2Goals,
      usesEventScore: true,
    };
  }

  return {
    team1Goals: storedTeam1Goals,
    team2Goals: storedTeam2Goals,
    usesEventScore: false,
  };
}

export function resolveDisplayedGameOutcome({ result, gameOutcome, matchEvents }) {
  const { team1Goals, team2Goals, usesEventScore } = resolveDisplayedScore(result, matchEvents);

  if (usesEventScore) {
    return deriveOutcomeFromScore(team1Goals, team2Goals);
  }

  const normalizedOutcome = String(gameOutcome || '').trim();
  return normalizedOutcome || deriveOutcomeFromScore(team1Goals, team2Goals);
}

export function resolveLatestTeamIndexFromMatchEvents(matchEvents, playerEmail, fallbackTeamIndex = null) {
  const targetEmail = normalizeEmail(playerEmail);
  if (!targetEmail) {
    return normalizeTeamIndex(fallbackTeamIndex);
  }

  let resolvedTeamIndex = normalizeTeamIndex(fallbackTeamIndex);

  sortMatchEvents(matchEvents).forEach((event) => {
    if (event?.type !== 'team_switch') return;
    if (normalizeEmail(event?.playerEmail) !== targetEmail) return;

    const nextTeamIndex = normalizeTeamIndex(event?.toTeamIndex);
    if (nextTeamIndex !== null) {
      resolvedTeamIndex = nextTeamIndex;
    }
  });

  return resolvedTeamIndex;
}

export function resolveParticipantTeamIndex({
  matchEvents,
  playerEmail,
  fallbackTeamIndex = null,
  attendance,
}) {
  if (!isParticipatingAttendance(attendance)) {
    return null;
  }

  return resolveLatestTeamIndexFromMatchEvents(matchEvents, playerEmail, fallbackTeamIndex);
}

function getTopScorerDisplayName(player) {
  const fullName = [player?.first_name, player?.last_name]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ');
  const formattedFullName = formatSummaryName(fullName);

  if (formattedFullName) {
    return formattedFullName;
  }

  return formatSummaryName(player?.email) || player?.email || 'Unknown player';
}

/**
 * Email -> User id, taken from the players embedded in a game.
 *
 * Match events identify a player by email only, while the team entries carry
 * the account id. This is the join between them, so a name in the timeline
 * can link to the same profile a name in the lineup does.
 */
export function buildUserIdByEmail(teams = []) {
  const userIdByEmail = new Map();

  (Array.isArray(teams) ? teams : []).forEach((team) => {
    (Array.isArray(team) ? team : []).forEach((player) => {
      const email = normalizeEmail(player?.email);
      const userId = String(player?.userId || '').trim();
      if (email && userId) {
        userIdByEmail.set(email, userId);
      }
    });
  });

  return userIdByEmail;
}

export function resolveTopScorersFromTeams(teams = []) {
  const scorersByKey = new Map();
  let topGoalCount = 0;

  (Array.isArray(teams) ? teams : []).forEach((team, teamIndex) => {
    (Array.isArray(team) ? team : []).forEach((player, playerIndex) => {
      if (!player || !isParticipatingAttendance(player?.attendance)) {
        return;
      }

      const goalCount = Number(player?.goals) || 0;
      if (goalCount <= 0) {
        return;
      }

      const normalizedEmail = normalizeEmail(player?.email);
      const displayName = getTopScorerDisplayName(player);
      const scorerKey = normalizedEmail || `${displayName.toLowerCase()}-${teamIndex}-${playerIndex}`;
      const existingScorer = scorersByKey.get(scorerKey);

      if (!existingScorer || goalCount > existingScorer.goals) {
        scorersByKey.set(scorerKey, {
          name: displayName,
          goals: goalCount,
          // Left undefined when the player has no account, so the name
          // renders as plain text rather than a dead link.
          userId: String(player?.userId || '').trim() || undefined,
        });
      }

      if (goalCount > topGoalCount) {
        topGoalCount = goalCount;
      }
    });
  });

  if (topGoalCount <= 0) {
    return {
      topScorers: [],
      goalCount: 0,
    };
  }

  return {
    topScorers: Array.from(scorersByKey.values()).filter((scorer) => scorer.goals === topGoalCount),
    goalCount: topGoalCount,
  };
}
